import { resolveRenderedSurfaces } from "./cardSurfaces";
import { Color, ensureContrast, readableText } from "./themes/color";

/** WCAG 2.1 AA for normal text. The label, status and date lines are small, so one bar for all. */
export const WCAG_AA_NORMAL_TEXT = 4.5;

/**
 * The text a metric card shows. `.metric-value` inherits the card foreground; the other three pin
 * their own color (`text-card-muted-foreground`, `.metric-status-*`), which is why a `text-white`
 * on the card never reaches them.
 */
export const METRIC_TEXT_SELECTORS = [
  ".metric-value",
  ".metric-label",
  ".metric-status",
  ".metric-date",
] as const;

const WHITE = Color.parse("#ffffff");
const NEAR_BLACK = Color.parse("#111827");

/**
 * Markers on an element whose color this pass overrode, so a later pass can tell its own override
 * from a color render JS set and measure the authored color again rather than its correction.
 */
const OVERRIDE_DATASET_KEY = "coeffContrastColor";
const AUTHORED_DATASET_KEY = "coeffContrastAuthoredColor";

/** A translucent text color (`text-card-foreground/60`) reads as what it paints on the surface. */
const perceived = (foreground: Color, surface: Color): Color => foreground.compositeOver(surface);

const clearsEverySurface = (
  candidate: Color,
  surfaces: readonly Color[],
  target: number
): boolean =>
  surfaces.every(surface => perceived(candidate, surface).contrastRatio(surface) >= target);

/**
 * The authored hue at every lightness, for the surfaces no nudge or extreme can satisfy (a gradient
 * running light to dark): somewhere in the middle is the color that is least bad on both ends.
 */
const lightnessSweep = (foreground: Color): Color[] => {
  const { h, s } = foreground.hsl;
  return Array.from({ length: 21 }, (_, i) => Color.fromHSL({ h, s, l: i / 20 }));
};

const minContrast = (candidate: Color, surfaces: readonly Color[]): number =>
  Math.min(...surfaces.map(surface => perceived(candidate, surface).contrastRatio(surface)));

/**
 * The color a text element should render in so it stays readable on every surface of the card,
 * or `null` when it already does (or when nothing can be measured, so authored styling wins).
 *
 * Hue is kept wherever it can be: each candidate is first `ensureContrast`'s lightness nudge of
 * the authored color, so a green status line stays green on a dark card. Only when no nudge
 * clears every surface does it fall back to the near-black / near-white that always does. A
 * gradient that runs light to dark has no single color that passes both ends; the candidate that
 * is least bad across all surfaces is used then, and only if it beats what the card already has.
 *
 * Measured at full strength, as every other contrast safeguard in the viewer is: the date line's
 * `opacity-70` is a deliberate de-emphasis the themes apply to their own cards too, and scoring
 * through it would have this pass recolor untouched cards on the default theme.
 */
export const readableMetricTextColor = (
  foreground: Color | null | undefined,
  surfaces: readonly Color[],
  target = WCAG_AA_NORMAL_TEXT
): Color | null => {
  if (!foreground || surfaces.length === 0) {
    return null;
  }
  if (clearsEverySurface(foreground, surfaces, target)) {
    return null;
  }
  // Hardest surface first, so the hue-preserving nudge aims at the stop most likely to fail.
  const bySeverity = [...surfaces].sort(
    (a, b) => perceived(foreground, a).contrastRatio(a) - perceived(foreground, b).contrastRatio(b)
  );
  const candidates = [
    ...bySeverity.map(surface => ensureContrast(foreground, surface, target)),
    ...bySeverity.map(surface => readableText(surface)),
    WHITE,
    NEAR_BLACK,
    ...lightnessSweep(foreground),
  ];
  const passing = candidates.find(candidate => clearsEverySurface(candidate, surfaces, target));
  if (passing) {
    return passing;
  }
  const best = candidates.reduce((current, candidate) =>
    minContrast(candidate, surfaces) > minContrast(current, surfaces) ? candidate : current
  );
  return minContrast(best, surfaces) > minContrast(foreground, surfaces) ? best : null;
};

/**
 * Put back whatever color the element had before this pass overrode it, so the authored color is
 * what gets measured. A color render JS set since (the inline value no longer matches the
 * override) is authored, and is left alone.
 */
const restoreAuthoredColor = (element: HTMLElement): void => {
  const override = element.dataset[OVERRIDE_DATASET_KEY];
  if (override === undefined) {
    return;
  }
  if (element.style.color === override) {
    element.style.color = element.dataset[AUTHORED_DATASET_KEY] ?? "";
  }
  delete element.dataset[OVERRIDE_DATASET_KEY];
  delete element.dataset[AUTHORED_DATASET_KEY];
};

/**
 * Deterministic contrast check for a metric card's text (ENG-8813).
 *
 * Runs after render JS has painted the card. Whatever the color source — a `bg-*` / gradient
 * class the agent added for a "colorful" card, a `text-*` class it hand-picked, or the theme's
 * muted and semantic tokens that were tuned for the theme's own card surface — each text element
 * is measured against every surface the card really paints, and any that falls below WCAG AA gets
 * an inline color that clears it. The background is never touched: it is what the user asked for.
 *
 * Fails open: with no resolvable surface (detached node, nothing opaque up the tree) or an
 * unparseable computed color, the element keeps what it had.
 */
export const applyMetricTextContrast = (cardElement: HTMLElement): void => {
  const view = cardElement.ownerDocument.defaultView;
  if (!view) {
    return;
  }
  const surfaces = resolveRenderedSurfaces(cardElement);
  for (const selector of METRIC_TEXT_SELECTORS) {
    cardElement.querySelectorAll<HTMLElement>(selector).forEach(element => {
      restoreAuthoredColor(element);
      if (surfaces.length === 0) {
        return;
      }
      const foreground = Color.tryParse(view.getComputedStyle(element).color);
      // (Measured in the element's own realm, as cardSurfaces does; the card lives in the iframe.)
      const corrected = readableMetricTextColor(foreground, surfaces);
      if (!corrected) {
        return;
      }
      element.dataset[AUTHORED_DATASET_KEY] = element.style.color;
      element.style.color = corrected.toHexString();
      // Browsers normalise the inline value (hex → rgb()), so record what was actually stored.
      element.dataset[OVERRIDE_DATASET_KEY] = element.style.color;
    });
  }
};
